import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  Injectable,
  NgModule,
  NgZone,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import {
  NgbAccordionModule,
  NgbCalendar,
  NgbCollapseModule,
  NgbDateAdapter,
  NgbDateNativeUTCAdapter,
  NgbDateParserFormatter,
  NgbDatepickerConfig,
  NgbDatepickerModule,
  NgbDateStruct,
  NgbDropdownModule,
  NgbInputDatepicker,
  NgbInputDatepickerConfig,
  NgbModalConfig,
  NgbModalModule,
  NgbNavModule,
  NgbPaginationModule,
  NgbProgressbarModule,
  NgbTimepickerModule,
  NgbTooltipConfig,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { isExists } from 'date-fns';

const currentYear = new Date().getFullYear();

@Injectable()
export class DateStringAdapter extends NgbDateAdapter<Date | string> {
  constructor(private readonly ngbDateNativeUTCAdapter: NgbDateNativeUTCAdapter) {
    super();
  }

  override fromModel(value: Date | string | null): NgbDateStruct {
    if (value && value instanceof Date) {
      return this.ngbDateNativeUTCAdapter.fromModel(value);
    }
    if (value && typeof value === 'string' && value.length >= 10) {
      const date = new Date(value);
      return this.ngbDateNativeUTCAdapter.fromModel(date);
    }
    return null;
  }

  override toModel(value: NgbDateStruct): string {
    if (value) {
      return this.ngbDateNativeUTCAdapter.toModel(value)
        ? this.ngbDateNativeUTCAdapter.toModel(value).toISOString().split('T')[0]
        : null;
    }
  }
}

@Injectable()
export class DatepickerConfig extends NgbDatepickerConfig {
  constructor() {
    super();
    this.minDate = {
      year: currentYear - 100,
      month: 1,
      day: 1,
    };
    this.maxDate = {
      year: currentYear + 10,
      month: 12,
      day: 31,
    };
    this.navigation = 'select';
    this.outsideDays = 'visible';
  }
}

@Injectable()
export class InputDatepickerConfig extends NgbInputDatepickerConfig {
  constructor() {
    super();
    this.minDate = {
      year: currentYear - 100,
      month: 1,
      day: 1,
    };
    this.maxDate = {
      year: currentYear + 10,
      month: 12,
      day: 31,
    };
    this.navigation = 'select';
    this.outsideDays = 'visible';
    this.autoClose = true;
    this.placement = ['bottom-end', 'bottom-start', 'top-end', 'top-start'];
  }
}

@Directive({
  selector: 'input[ndtDatepicker]',
  exportAs: 'ndtDatepicker',
  standalone: true,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputDatepicker), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => InputDatepicker), multi: true },
    { provide: NgbDatepickerConfig, useExisting: InputDatepickerConfig },
  ],
})
export class InputDatepicker extends NgbInputDatepicker {
  constructor(
    private readonly parserFormatter: NgbDateParserFormatter,
    private readonly elRef: ElementRef<HTMLInputElement>,
    private readonly vcRef: ViewContainerRef,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly calendar: NgbCalendar,
    private readonly dateAdapter: NgbDateAdapter<any>,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly changeDetector: ChangeDetectorRef,
    readonly config: NgbInputDatepickerConfig,
  ) {
    super(parserFormatter, elRef, vcRef, renderer, ngZone, calendar, dateAdapter, document, changeDetector, config);
  }

  override validate(c: AbstractControl): ValidationErrors | null {
    if (super.validate(c)) {
      return super.validate(c);
    }
    const { value } = c;
    // eslint-disable-next-line eqeqeq
    if (value != null && typeof value === 'string') {
      const delimitedString = value.slice(0, 10).split('-');
      const [year, month, day] = delimitedString;

      if (!isExists(+year, +month - 1, +day)) {
        return { ngbDate: { invalid: value } };
      }
    }
    return null;
  }
}

const ngbModules = [
  NgbAccordionModule,
  NgbCollapseModule,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModalModule,
  NgbNavModule,
  NgbPaginationModule,
  NgbProgressbarModule,
  NgbTimepickerModule,
  NgbTooltipModule,
];

@NgModule({
  exports: [...ngbModules, InputDatepicker],
  imports: [...ngbModules, InputDatepicker],
  providers: [
    NgbDateNativeUTCAdapter,
    { provide: NgbDatepickerConfig, useClass: DatepickerConfig },
    { provide: NgbInputDatepickerConfig, useClass: InputDatepickerConfig },
    { provide: NgbDateAdapter, useClass: DateStringAdapter },
    { provide: NgbInputDatepicker, useClass: InputDatepicker },
  ],
})
export class NgBootstrapComponentsModule {
  constructor(
    readonly ngbModalConfig: NgbModalConfig,
    readonly ngbTooltipConfig: NgbTooltipConfig,
  ) {
    ngbModalConfig.modalDialogClass = 'modal-dialog-centered modal-dialog-scrollable';
    ngbTooltipConfig.container = 'body';
  }
}
